import { useCallback, useEffect, useMemo, useState } from 'react';
import { Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { useApi } from 'hooks/useApi';
import FinanceInvoices from 'libs/api/finance-invoices';
import UIHelper from 'helpers/UIHelper';
import Helpers from '../../../../helpers';
import { invoiceAdapter } from './edit/invoice.adapter';
import { getTotals } from './edit/invoiceCalculations';

const useSingleInvoice = () => {
  const { t } = useTranslation();
  const { singleItem, loading } = useApi({
    isMainPage: false,
    api: new FinanceInvoices(),
    singleResourceKey: 'invoices',
  });

  const [items, setItems] = useState([]);

  useEffect(() => {
    if (singleItem?.allData?.invoices) {
      setItems(invoiceAdapter(singleItem.allData.invoices));
    } else {
      setItems([]);
    }
  }, [singleItem]);

  const isWeekClosed = items[0]?.week_closed ?? false;
  const companyName =
    singleItem?.allData?.company?.name || items[0]?.company?.name || '';
  const canUpdate = singleItem?.allData?.can_update;
  const canApprove = singleItem?.allData?.can_approve;

  const columns = useMemo(
    () => [
      {
        title: t('Date'),
        dataIndex: 'invoice_date',
        minWidth: 120,
        width: 120,
        render: (row) => UIHelper.formatedDate(row, 'dddd, DD MMMM YYYY'),
      },
      {
        title: t('Netto (€)'),
        dataIndex: 'taxable',
        width: 120,
      },
      {
        title: t('Lordo (€) %'),
        dataIndex: 'taxable_vat',
        width: 120,
      },
      {
        title: t('SCONTRINI'),
        dataIndex: 'receipts',
        width: 120,
      },
      {
        title: t('PEZZI'),
        dataIndex: 'pieces',
        width: 120,
      },
      {
        title: t('VISITATORI'),
        dataIndex: 'visitors',
        width: 120,
      },
      {
        title: t('ATV'),
        dataIndex: 'atv',
        width: 80,
      },
      {
        title: t('UPT'),
        dataIndex: 'upt',
        width: 80,
      },
      {
        title: t('Conversion Rate'),
        dataIndex: 'conversion_rate',
        width: 140,
      },
      {
        title: t('GIORNO DI CHIUSURA'),
        dataIndex: 'day_off',
        align: 'right',
        width: 100,
        render: (value) => (value ? t('Yes') : t('No')),
      },
    ],
    [t],
  );

  const tableSummary = useCallback(
    (pageData) => {
      const {
        taxable,
        taxable_vat,
        receipts,
        pieces,
        visitors,
        atv,
        upt,
        conversion_rate,
      } = getTotals(pageData);
      return (
        <Table.Summary fixed>
          <Table.Summary.Row style={{ fontWeight: 'bold' }}>
            <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
            <Table.Summary.Cell>
              {Helpers.numberWithCommas(taxable)}
            </Table.Summary.Cell>
            <Table.Summary.Cell>
              {Helpers.numberWithCommas(taxable_vat)}
            </Table.Summary.Cell>
            <Table.Summary.Cell>
              {Helpers.numberWithCommas(receipts)}
            </Table.Summary.Cell>
            <Table.Summary.Cell>
              {Helpers.numberWithCommas(pieces)}
            </Table.Summary.Cell>
            <Table.Summary.Cell>
              {Helpers.numberWithCommas(visitors)}
            </Table.Summary.Cell>
            <Table.Summary.Cell>
              {Helpers.numberWithCommas(atv)}
            </Table.Summary.Cell>
            <Table.Summary.Cell>
              {Helpers.numberWithCommas(upt)}
            </Table.Summary.Cell>
            <Table.Summary.Cell>
              {Helpers.numberWithCommas(conversion_rate)}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      );
    },
    [],
  );

  return {
    singleItem,
    loading,
    data: items,
    columns,
    tableSummary,
    pageTitle: companyName || t('Fatturato da approvare'),
    isWeekClosed,
    canUpdate,
    canApprove,
  };
};

export default useSingleInvoice;
